/** @format */

import { apiSlice } from "../apiSlice";
import { Locale } from "../../i18n/init";
import {
  ResourceReference,
  UserResource,
  UserResourceReference,
} from "../../types";
import { store } from "../store";
import { DeepPartial } from "@reduxjs/toolkit";

type Resource = {
  code: string;
  locale: Locale;
  copy: number;
};

interface CreateResponse {
  userResourceReference: ResourceReference;
  message: string;
}

interface UpdateUserResourceRef {
  result: UserResourceReference;
  message: string;
  UUID: string;
}

/** API Endpoints related to User Resources and User Resource references */
export const userResourceApiSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    /**
     * createUserResourceReference mutation (POST)
     * @param {Partial<Resource>} - The resource to be duplicated and converted to a user resource.
     * @returns {CreateResponse} the created user resource.
     */
    createUserResourceReference: build.mutation<
      CreateResponse,
      Partial<Resource>
    >({
      query: (body) => {
        return {
          url: `user-resource/create`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: [{ type: "UserResourceRef", id: "LIST" }],
    }),

    /**
     * getUserResourceReference query (GET)
     * @param {string} code - The user resource's project code
     * @param {Locale} locale - The locale of the user resource
     * @param {number} copy - The copy number of the resource
     * @returns {UserResourceReference} the fetched user resource reference.
     */
    getUserResourceReference: build.query<
      UserResourceReference,
      { code: string; locale: Locale; copy: number }
    >({
      query: (args) => {
        const { code, locale, copy } = args;
        return {
          url: `user-resource/get-reference/${code}/${locale}/${copy}`,
        };
      },
      providesTags: (result) => [
        { type: "UserResourceRef" as const, id: result?.UUID },
      ],
    }),

    /**
     * getForUser query (GET)
     * @returns {UserResourceReference[]} the user resources created from the current logged in user.
     */
    getForUser: build.query<UserResourceReference[], void>({
      query: () => `user-resource/get-for-user`,
      transformResponse: (response: {
        userResourceRefs: UserResourceReference[];
      }) => response.userResourceRefs,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ UUID }) => ({
                type: "UserResourceRef" as const,
                id: UUID,
              })),
              { type: "UserResourceRef", id: "LIST" },
            ]
          : [{ type: "UserResourceRef", id: "LIST" }],
    }),

    /**
     * getUserResource query (GET)
     * @param {string} code - The user resource's project code
     * @param {Locale} locale - The locale of the user resource
     * @param {number} copy - The copy number of the resource
     * @returns {UserResourceReference} the fetched user resource.
     */
    getUserResource: build.query<
      UserResource,
      { code: string; locale: Locale; copy: number; email: string }
    >({
      query: (args) => {
        const { code, locale, copy, email } = args;
        // If logged in, send request to auth endpoint
        if (store.getState().auth.userToken?.token) {
          return {
            url: `user-resource/auth/get/${code}/${locale}/${copy}/${email}`,
          };
        }
        return {
          url: `user-resource/get/${code}/${locale}/${copy}/${email}`,
        };
      },
      transformResponse: (response: { userResource: UserResource }) =>
        response.userResource,
    }),

    /**
     * updateUserResourceReference mutation (POST)
     * @param {Partial<UserResourceReference>} - The new user resource reference item.
     * @returns {UpdateUserResourceRef} the updated user resource reference.
     */
    updateUserResourceReference: build.mutation<
      UpdateUserResourceRef,
      {
        diff: DeepPartial<UserResourceReference>;
        userEmailHash: string;
        locale: Locale;
        copy: number;
        code: string;
        primaryKey: { PK: string; SK: string };
      }
    >({
      query: (body) => {
        return {
          url: `user-resource/save-resource-ref/`,
          method: "POST",
          body: body,
        };
      },
      invalidatesTags: (result) => [
        { type: "UserResourceRef" as const, id: result?.UUID },
        { type: "UserResourceRef", id: "LIST" },
      ],
    }),

    /**
     * deleteUserResource mutation (DELETE)
     * @param {string} code - The user resource's project code
     * @param {Locale} locale - The locale of the user resource
     * @param {number} copy - The copy number of the resource
     * @returns message that resource has been deleted
     */
    deleteUserResource: build.mutation<string, Resource>({
      query: (args) => {
        const { copy, locale, code } = args;
        return {
          url: `user-resource/delete-resource/${code}/${locale}/${copy}`,
          method: "DELETE",
        };
      },
      transformResponse: (response: { message: string }) => response.message,
      invalidatesTags: [
        "UserResourceRef",
        { type: "UserResourceRef", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetUserResourceReferenceQuery,
  useCreateUserResourceReferenceMutation,
  useUpdateUserResourceReferenceMutation,
  useDeleteUserResourceMutation,
  useGetUserResourceQuery,
  useGetForUserQuery,
} = userResourceApiSlice;
